import { AxiosError } from 'axios';
import _ from 'lodash';

import { CONFLICT_TYPE, HTTP_RESPONSE_STATUS_CODES } from 'constants/http';

/**
 * Returns boolean if response conflict error type is equal to passed conflictType
 * @param {object} response
 * @param {string} conflictType
 * @returns {boolean}
 */

export const checkIsSpecificConflictError = (response, conflictType = CONFLICT_TYPE.DEFAULT) => {
  const isConflictResponse = response?.status === HTTP_RESPONSE_STATUS_CODES.CONFLICT;
  const isExpectedConflictType = conflictType === response?.data?.conflictType;

  return isConflictResponse && isExpectedConflictType;
};

export const generateUnauthorizedAxiosError = () => {
  return new AxiosError('Unauthorized', HTTP_RESPONSE_STATUS_CODES.UNAUTHORIZED, null, null, {
    status: HTTP_RESPONSE_STATUS_CODES.UNAUTHORIZED,
  });
};

const readBlobText = blob =>
  _.isFunction(blob.text)
    ? blob.text()
    : new Promise((resolve, reject) => {
        const reader = new FileReader();

        reader.onload = () => resolve(reader.result);
        reader.onerror = () => reject(reader.error);
        reader.readAsText(blob);
      });

const parseJsonSafely = text => {
  try {
    return JSON.parse(text);
  } catch {
    return null;
  }
};

const pickMessage = body => {
  const message = body?.error?.message || body?.message;

  if (!_.isString(message) || !message.trim()) {
    return null;
  }

  const nested = message.trimStart().startsWith('{') ? pickMessage(parseJsonSafely(message)) : null;

  return nested || message;
};

/**
 * Extracts the server-provided message from an axios error. Requests made with
 * responseType 'blob' deliver the JSON error body as a Blob, so it is read as text first.
 * Never throws; resolves to null when no message is available.
 * @param {object} err
 * @returns {Promise<string|null>}
 */
export const getServerErrorMessage = async err => {
  try {
    const data = err?.response?.data;

    if (data instanceof Blob) {
      return pickMessage(parseJsonSafely(await readBlobText(data)));
    }

    if (_.isString(data)) {
      return pickMessage(parseJsonSafely(data));
    }

    return pickMessage(data);
  } catch {
    return null;
  }
};
